import { Abstract } from '@/module/Author/Abstract';
import { Article } from '@/module/Author/Article';
import { Book } from '@/module/Author/Book';
import { Chapter } from '@/module/Author/Chapter';
import { Committee } from '@/module/Author/Committee';
import { Course } from '@/module/Author/Course';
import { DoctoralCommittee } from '@/module/Author/DoctoralCommittee';
import { EditorialTeam } from '@/module/Author/EditorialTeam';
import { Patent } from '@/module/Author/Patent';
import { Proceeding } from '@/module/Author/Proceeding';
import { Project } from '@/module/Author/Project';
import { Translation } from '@/module/Author/Translation';

import { BasicUser, PersistentFile } from '@feature/utils';

export interface EvaluationReport {
  id: number;
  title: string;
  year: number;
  open: boolean;
  closingDate: string;
  headOfDisciplineClosingDate: string;
  headOfDepartmentClosingDate: string;
}

export interface EvaluationReportAuthor {
  id: number;
  report: EvaluationReport;
  user: BasicUser;
  startedAt: string;
  completedAt: string | null;

  collegiateEvaluationFile: PersistentFile | null;
  chiefEvaluationFile: PersistentFile | null;

  disciplineAdminSignedFile: PersistentFile | null;
  departmentAdminSignedFile: PersistentFile | null;
}

export type EvaluationCategory = 'DIDACTIC_PROFESSIONAL_ACTIVITY' | 'SCIENTIFIC_ACTIVITY' | 'COMMUNITY_REACH_ACTIVITY';

export interface EvaluationPartialCriteria {
  title: string;
  legend: string;
  orderNo: number;
  criteriaType: EvaluationCriteriaType;
  category: EvaluationCategory;

  dataEntryHint: string | null;
}

export interface EvaluationCriteriaDiscriminator {
  code: string;
  name: string;
}

export interface EvaluationCriteria extends EvaluationPartialCriteria {
  discriminatorList: EvaluationCriteriaDiscriminator[];
}

export interface EvaluationCriteriaOverview {
  criteria: EvaluationPartialCriteria;
  authorPoints: number;
  headOfDisciplinePoints: number;
  headOfDepartmentPoints: number;

  formattedAuthorPoints: string;
  formattedHeadOfDisciplinePoints: string;
  formattedHeadOfDepartmentPoints: string;
}

export interface EvaluationAuthorCategoryOverview {
  data: EvaluationCriteriaOverview[];

  totalAuthorPoints: number;
  totalAuthorCategoryPoints: number;
  totalAuthorMeritPoints: number;

  totalHeadOfDisciplinePoints: number;
  totalHeadOfDisciplineCategoryPoints: number;
  totalHeadOfDisciplineMeritPoints: number;

  totalHeadOfDepartmentPoints: number;
  totalHeadOfDepartmentCategoryPoints: number;
  totalHeadOfDepartmentMeritPoints: number;

  categoryWeight: number;
  meritWeight: number;
}

export interface EvaluationOverview {
  map: Record<EvaluationCategory, EvaluationAuthorCategoryOverview>;

  totalAuthorPoints: number;
  totalAuthorCategoryPoints: number;
  totalAuthorMeritPoints: number;

  totalHeadOfDisciplinePoints: number;
  totalHeadOfDisciplineCategoryPoints: number;
  totalHeadOfDisciplineMeritPoints: number;

  totalHeadOfDepartmentPoints: number;
  totalHeadOfDepartmentCategoryPoints: number;
  totalHeadOfDepartmentMeritPoints: number;
}

export type EvaluationCriteriaType =
  | 'FREE_ANSWER'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'SELECT_ABSTRACT'
  | 'SELECT_ARTICLE'
  | 'SELECT_BOOK'
  | 'SELECT_CHAPTER'
  | 'SELECT_COURSE'
  | 'SELECT_COMMITTEE'
  | 'SELECT_PROJECT'
  | 'SELECT_PATENT'
  | 'SELECT_TRANSLATION'
  | 'SELECT_PROCEEDING'
  | 'SELECT_DOCTORAL_COMMITTEE'
  | 'SELECT_EDITORIAL_TEAM';

interface BaseEvaluationCriteriaDataItem {
  id: number;
  note: string;
  link: string | null;
  file: PersistentFile;
  points: number;

  discriminator: EvaluationCriteriaDiscriminator;

  criteriaType: EvaluationCriteriaType;
}

export type EvaluationCriteriaDataItem =
  | EvaluationCriteriaFreeItem
  | EvaluationCriteriaBooleanItem
  | EvaluationCriteriaNumberItem
  | EvaluationCriteriaCourseItem
  | EvaluationCriteriaAbstractItem
  | EvaluationCriteriaArticleItem
  | EvaluationCriteriaBookItem
  | EvaluationCriteriaChapterItem
  | EvaluationCriteriaCommitteeItem
  | EvaluationCriteriaPatentItem
  | EvaluationCriteriaProjectItem
  | EvaluationCriteriaTranslationItem
  | EvaluationCriteriaProceedingItem
  | EvaluationCriteriaDoctoralCommitteeItem
  | EvaluationCriteriaEditorialTeamItem;

export function isEvaluationCriteriaFreeItem(item: EvaluationCriteriaDataItem): item is EvaluationCriteriaFreeItem {
  return item.criteriaType === 'FREE_ANSWER';
}

export function isEvaluationCriteriaBooleanItem(
  item: EvaluationCriteriaDataItem,
): item is EvaluationCriteriaBooleanItem {
  return item.criteriaType === 'BOOLEAN';
}

export function isEvaluationCriteriaNumberItem(item: EvaluationCriteriaDataItem): item is EvaluationCriteriaNumberItem {
  return item.criteriaType === 'NUMBER';
}

export function isEvaluationCriteriaAbstractItem(
  item: EvaluationCriteriaDataItem,
): item is EvaluationCriteriaAbstractItem {
  return item.criteriaType === 'SELECT_ABSTRACT';
}

export function isEvaluationCriteriaArticleItem(
  item: EvaluationCriteriaDataItem,
): item is EvaluationCriteriaArticleItem {
  return item.criteriaType === 'SELECT_ARTICLE';
}

export function isEvaluationCriteriaBookItem(item: EvaluationCriteriaDataItem): item is EvaluationCriteriaBookItem {
  return item.criteriaType === 'SELECT_BOOK';
}

export function isEvaluationCriteriaTranslationItem(
  item: EvaluationCriteriaDataItem,
): item is EvaluationCriteriaTranslationItem {
  return item.criteriaType === 'SELECT_TRANSLATION';
}

export function isEvaluationCriteriaChapterItem(
  item: EvaluationCriteriaDataItem,
): item is EvaluationCriteriaChapterItem {
  return item.criteriaType === 'SELECT_CHAPTER';
}

export function isEvaluationCriteriaCommitteeItem(
  item: EvaluationCriteriaDataItem,
): item is EvaluationCriteriaCommitteeItem {
  return item.criteriaType === 'SELECT_COMMITTEE';
}

export function isEvaluationCriteriaCourseItem(item: EvaluationCriteriaDataItem): item is EvaluationCriteriaCourseItem {
  return item.criteriaType === 'SELECT_COURSE';
}

export function isEvaluationCriteriaProjectItem(
  item: EvaluationCriteriaDataItem,
): item is EvaluationCriteriaProjectItem {
  return item.criteriaType === 'SELECT_PROJECT';
}

export function isEvaluationCriteriaPatentItem(item: EvaluationCriteriaDataItem): item is EvaluationCriteriaPatentItem {
  return item.criteriaType === 'SELECT_PATENT';
}

export function isEvaluationCriteriaDoctoralCommitteeItem(
  item: EvaluationCriteriaDataItem,
): item is EvaluationCriteriaDoctoralCommitteeItem {
  return item.criteriaType === 'SELECT_DOCTORAL_COMMITTEE';
}

export function isEvaluationCriteriaEditorialTeamItem(
  item: EvaluationCriteriaDataItem,
): item is EvaluationCriteriaEditorialTeamItem {
  return item.criteriaType === 'SELECT_EDITORIAL_TEAM';
}

export function isEvaluationCriteriaProceedingItem(
  item: EvaluationCriteriaDataItem,
): item is EvaluationCriteriaProceedingItem {
  return item.criteriaType === 'SELECT_PROCEEDING';
}

export interface EvaluationCriteriaFreeItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'FREE_ANSWER';
  activityDescription: string;
}

export interface EvaluationCriteriaBooleanItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'BOOLEAN';
}

export interface EvaluationCriteriaNumberItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'NUMBER';
  number: number;
}

export interface EvaluationCriteriaAbstractItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'SELECT_ABSTRACT';
  abstractItem: Abstract;
}

export interface EvaluationCriteriaArticleItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'SELECT_ARTICLE';
  article: Article;
}

export interface EvaluationCriteriaBookItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'SELECT_BOOK';
  book: Book;
}

export interface EvaluationCriteriaChapterItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'SELECT_CHAPTER';
  chapter: Chapter;
}

export interface EvaluationCriteriaProceedingItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'SELECT_PROCEEDING';
  proceeding: Proceeding;
}

export interface EvaluationCriteriaTranslationItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'SELECT_TRANSLATION';
  translation: Translation;
}

export interface EvaluationCriteriaCourseItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'SELECT_COURSE';
  course: Course;
}

export interface EvaluationCriteriaCommitteeItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'SELECT_COMMITTEE';
  committee: Committee;
}

export interface EvaluationCriteriaProjectItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'SELECT_PROJECT';
  project: Project;
}

export interface EvaluationCriteriaPatentItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'SELECT_PATENT';
  patent: Patent;
}

export interface EvaluationCriteriaDoctoralCommitteeItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'SELECT_DOCTORAL_COMMITTEE';
  committee: DoctoralCommittee;
}

export interface EvaluationCriteriaEditorialTeamItem extends BaseEvaluationCriteriaDataItem {
  criteriaType: 'SELECT_EDITORIAL_TEAM';
  editorialTeam: EditorialTeam;
}

export interface EvaluationCriteriaDataItemRequest {
  note?: string | null;
  link: string | null;

  newFile: File | null;

  criteriaType: EvaluationCriteriaType;
}

export interface EvaluationCriteriaData {
  authorPoints: number;
  headOfDisciplinePoints: number;
  headOfDepartmentPoints: number;
  items: EvaluationCriteriaDataItem[];
}

export interface EvaluationAuthorDataWrapper {
  criteria: EvaluationCriteria;
  data: EvaluationCriteriaData;
}

export interface EvaluationItemOption<T> {
  option: T;
  selectable: boolean;
  mismatchedConditionType: string | null;
}

export interface EvaluationChangeValidationDatesRequest {
  closingDate: string | null;
  headOfDisciplineClosingDate: string | null;
  headOfDepartmentClosingDate: string | null;
}
